import { Loader, LoaderOptions } from 'google-maps';
import React, { useEffect, useState } from 'react';
import type { Field } from 'formik';
import { useFormikContext } from 'formik';
import { Input } from 'components/Modals/CategoryModal/styles';
import { AddressType } from 'data/requests/submitBIF';
import { BIFData } from 'data/types/BIF';
import { AddressContainer, SingleAddress } from './styles';

const options: LoaderOptions = {
  libraries: ['places'],
  version: 'weekly',
};

const loader = new Loader(import.meta.env.VITE_APP_GOOGLE_API_KEY, options);

type AddressProps = {
  Component: typeof Field;
  ComponentProps: {
    name: string;
    label: string;
    placeholder: string;
    disabled?: boolean;
  };
  setAddress?: (address: AddressType) => void;
};

const componentForm = {
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  administrative_area_level_1: 'short_name',
  country: 'short_name',
  postal_code: 'short_name',
};

const renames = {
  street_number: 'line1',
  route: 'line1',
  locality: 'city',
  administrative_area_level_1: 'state',
  country: 'country',
  postal_code: 'zip',
};

const extractAddress = (place: any) => {
  const address: any = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const component of place.address_components) {
    const addressType = component.types[0] as keyof typeof componentForm;
    if (componentForm[addressType]) {
      const val = component[componentForm[addressType]];
      if (address[renames[addressType]]) {
        address[renames[addressType]] = `${
          address[renames[addressType]]
        } ${val}`;
      } else {
        address[renames[addressType]] = val;
      }
    }
  }
  return address;
};

const formatAddress = (address: AddressType) => {
  return `${address.line1}, ${address.city}, ${address.state} ${address.zip}`;
};

const debounce = (fn: any, delay: number) => {
  let timer: any;
  return (...args: any) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

const Address: React.FC<AddressProps> = ({
  Component,
  ComponentProps,
  setAddress: setRealAddress,
}) => {
  const { setFieldValue } = useFormikContext();
  // This is an address selection autocomplete component
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [address, setAddress] = useState<string>('');
  const [places, setPlaces] = useState<
    Array<google.maps.places.AutocompletePrediction>
  >([]);
  const [placesService, setPlacesService] =
    useState<google.maps.places.PlacesService | null>(null);
  const [focused, setFocused] = useState<boolean>(false);
  const [initialLoaded, setInitialLoaded] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressType | null>(
    null,
  );

  useEffect(() => {
    loader.load().then(() => {
      const autocompleteService = new google.maps.places.AutocompleteService();
      setAutocomplete(autocompleteService);
      const loadedPlacesService = new google.maps.places.PlacesService(
        document.createElement('div'),
      );
      setPlacesService(loadedPlacesService);
    });
  }, []);

  const { values } = useFormikContext<BIFData>();

  useEffect(() => {
    const value = values?.[ComponentProps.name as keyof BIFData] as string;
    if (placesService?.getDetails && value && !initialLoaded) {
      setInitialLoaded(true);
      placesService?.textSearch({ query: value }, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          if (!results) {
            setAddress('');
            setFieldValue(ComponentProps.name, '');
            return;
          }

          const place = results[0];
          if (place) {
            placesService?.getDetails(
              { placeId: place.place_id as string },
              (selectedPlace, placeStatus) => {
                if (placeStatus === google.maps.places.PlacesServiceStatus.OK) {
                  const extractedAddress = extractAddress(selectedPlace);
                  if (setRealAddress) {
                    setRealAddress(extractedAddress);
                  }

                  const formattedAddress = formatAddress(extractedAddress);
                  setAddress(formattedAddress);
                  setFieldValue(ComponentProps.name, formattedAddress);
                  setInitialLoaded(true);
                }
              },
            );
          }
        }
      });
    }
  }, [
    values,
    initialLoaded,
    ComponentProps.name,
    placesService,
    setRealAddress,
    setFieldValue,
  ]);

  const fetch = React.useMemo(
    () =>
      debounce((givenAddress: string) => {
        if (autocomplete) {
          autocomplete?.getPlacePredictions(
            {
              input: givenAddress,
              componentRestrictions: { country: 'us' },
            },
            (results) => {
              if (!results) {
                setPlaces([]);
                return;
              }
              setPlaces(results);
            },
          );
        }
      }, 400),
    [autocomplete],
  );

  useEffect(() => {
    if (address.length > 0) {
      fetch(address);
    }
  }, [address, fetch]);

  return (
    <>
      <Component
        value={address}
        onChange={(e: any) => {
          setAddress(e.target.value);
          setFieldValue(ComponentProps.name, e.target.value);
          setSelectedAddress(null);
        }}
        as={Input}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (!selectedAddress) {
            setAddress('');
            setFieldValue(ComponentProps.name, '');
          }
        }}
        {...ComponentProps}
        disabled={ComponentProps?.disabled || !initialLoaded}
      />
      {focused && places.length > 0 && (
        <AddressContainer>
          {places.map((place) => (
            <SingleAddress
              key={place.place_id}
              onMouseDown={() => {
                placesService?.getDetails(
                  { placeId: place.place_id },
                  (selectedPlace, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK) {
                      const extractedAddress = extractAddress(selectedPlace);
                      if (setRealAddress) {
                        setRealAddress(extractedAddress);
                      }
                      setSelectedAddress(extractedAddress);

                      const formattedAddress = formatAddress(extractedAddress);
                      setAddress(formattedAddress);
                      setFieldValue(ComponentProps.name, formattedAddress);
                    }
                  },
                );
              }}
            >
              {place.description}
            </SingleAddress>
          ))}
        </AddressContainer>
      )}
    </>
  );
};

Address.defaultProps = {
  setAddress: undefined,
};

export default Address;
